import i18n from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";

import { useMarkWarningConfirmedMutation, useMarkWarningDisplayedMutation, useSaveWarningConfirmationsMutation } from "src/serverApi";
import { useTranslation } from "src/translations";
import type { UserInvestorQuestionnaire, WarningConfirmation, WarningConfirmationPayload, WarningConfirmationType } from "src/types";

export function getWarningTranslationKey(warningType: WarningConfirmationType) {
    return warningType === "WARNING_GENERIC" || !i18n.exists(`verification.INVESTOR_QUESTIONNAIRE.result.warning.${warningType}`)
        ? "generic"
        : warningType;
}

export function getLatestSubmissionWarnings(questionnaire: UserInvestorQuestionnaire | undefined): WarningConfirmation[] {
    const submissions = questionnaire?.questionnaireSubmissions ?? [];
    if (submissions.length === 0) return [];

    const latest = submissions.reduce((latestSoFar, submission) =>
        new Date(submission.submissionTimestamp) > new Date(latestSoFar.submissionTimestamp) ? submission : latestSoFar,
    );

    return latest.warningsConfirmations ?? [];
}

export type WarningSection = {
    warningType: WarningConfirmationType;
    translationKey: string;
    confirmed: boolean;
};

export const useQuestionnaireWarnings = (
    documentId: string,
    warningCodes: WarningConfirmationType[],
    existingConfirmations: WarningConfirmation[],
) => {
    const { t } = useTranslation();
    const [saveWarningConfirmations] = useSaveWarningConfirmationsMutation();
    const [markWarningDisplayed] = useMarkWarningDisplayedMutation();
    const [markWarningConfirmed] = useMarkWarningConfirmedMutation();
    const [confirmedTypes, setConfirmedTypes] = useState<WarningConfirmationType[]>(() =>
        existingConfirmations.filter((confirmation) => confirmation.accepted).map((confirmation) => confirmation.warningType),
    );
    const [confirmingType, setConfirmingType] = useState<WarningConfirmationType | null>(null);
    const hasInitialized = useRef(false);

    const typesToShow = useMemo<WarningConfirmationType[]>(
        () => (warningCodes.length > 0 ? warningCodes : ["WARNING_GENERIC"]),
        [warningCodes],
    );

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const alreadySavedTypes = new Set(existingConfirmations.map((confirmation) => confirmation.warningType));
        const newTypes = typesToShow.filter((warningType) => !alreadySavedTypes.has(warningType));
        if (newTypes.length === 0) return;

        const warnings: WarningConfirmationPayload[] = newTypes.map((warningType) => {
            const translationKey = getWarningTranslationKey(warningType);
            return {
                warningType,
                warningContent: {
                    title: t(`verification.INVESTOR_QUESTIONNAIRE.result.warning.${translationKey}.title`),
                    text: t(`verification.INVESTOR_QUESTIONNAIRE.result.warning.${translationKey}.text`),
                    confirmation: t(`verification.INVESTOR_QUESTIONNAIRE.result.warning.${translationKey}.checkboxLabel`),
                },
            };
        });

        saveWarningConfirmations({ documentId, warnings });
        newTypes.forEach((warningType) => {
            markWarningDisplayed({ documentId, warningType });
        });
    }, [documentId, typesToShow, existingConfirmations, saveWarningConfirmations, markWarningDisplayed, t]);

    const confirmSection = async (warningType: WarningConfirmationType) => {
        setConfirmingType(warningType);
        await markWarningConfirmed({ documentId, warningType });
        setConfirmedTypes((prev) => [...prev, warningType]);
        setConfirmingType(null);
    };

    const sections: WarningSection[] = typesToShow.map((warningType) => ({
        warningType,
        translationKey: getWarningTranslationKey(warningType),
        confirmed: confirmedTypes.includes(warningType),
    }));

    return {
        sections,
        confirmSection,
        confirmingType,
        allConfirmed: sections.every((section) => section.confirmed),
    };
};
